import { isBot, useNavigator } from '@diagentech/exprworks-ui-components';
import React, { ComponentType } from 'react';

import { useAppSelector } from '@/App/hooks';
import { AppCookieConsent } from '@/components/layout/AppCookieConsent/AppCookieConsent';
import { SeoMetaInfo } from '@/components/seo/SeoMetaInfo';
import { selectAppConfig } from '@/features/config/selectAppConfig';
import { CookieConsentProvider } from '@/hooks/CookieConsentProvider';
import { LocaleProvider } from '@/hooks/LocaleProvider';

export const withAppProviders = <T extends object>(WrappedComponent: ComponentType<T>) => {
  const HOC = (props: T) => {
    const {
      localeCookieName,
      localeCookieExpiresInDays,
      consentCookieName,
      consentCookieVersion,
      consentCookieExpiresInDays,
    } = useAppSelector(selectAppConfig);
    const { userAgent } = useNavigator();

    if (!localeCookieName || !localeCookieExpiresInDays || !consentCookieName || !consentCookieVersion) {
      return null;
    }

    const component = (
      <>
        <SeoMetaInfo />
        <WrappedComponent {...props} />
      </>
    );

    if (localeCookieName && isBot(userAgent)) {
      return (
        <LocaleProvider localeCookieName={localeCookieName} localeCookieExpiresInDays={localeCookieExpiresInDays}>
          {component}
        </LocaleProvider>
      );
    }

    return (
      <LocaleProvider localeCookieName={localeCookieName} localeCookieExpiresInDays={localeCookieExpiresInDays}>
        <CookieConsentProvider
          consentCookieName={consentCookieName}
          consentCookieVersion={consentCookieVersion}
          consentCookieExpiresInDays={consentCookieExpiresInDays}
        >
          <AppCookieConsent />
          {component}
        </CookieConsentProvider>
      </LocaleProvider>
    );
  };
  HOC.displayName = `withAppProviders(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return HOC;
};
